import { Box, Typography, Stack } from "@mui/material"
import { LabelLayoutSettingsValues } from "../types"
import { forwardRef } from "react"
import { QRCodeWithIcon } from "src/shared/components"
import { useTypedSelector } from "src/shared/store/store"
import { getDefaultLabelSettings } from "../barcodes-utils"
import { LotBarcodeT } from ".."

interface BarcodeLayoutProps {
  config: LabelLayoutSettingsValues
  barcodes: LotBarcodeT[]
}

const BarcodeLayout = forwardRef<HTMLDivElement, BarcodeLayoutProps>(({ config, barcodes }, ref) => {
  // Calculate the number of stickers to print
  // If batchNoTo is set, calculate count from batchNoFrom to batchNoTo
  // Otherwise, use total count
  // const batchNoFrom = config.batchNoFrom ?? 1
  // const batchNoTo = config.batchNoTo ?? config.total
  // const stickerCount =
  //   config.batchNoTo !== null && config.batchNoFrom !== null ? batchNoTo - batchNoFrom + 1 : config.total

  const tenant = useTypedSelector((state) => state.authentication.tenant)

  // Get label settings with defaults
  const settings = config ?? getDefaultLabelSettings()

  // Apply zoom to dimensions
  const labelWidth = settings.labelWidth * settings.zoom
  const labelHeight = settings.labelHeight * settings.zoom
  const qrSize = settings.qrCodeSize * settings.zoom
  const columnGap = settings.columnGap * settings.zoom
  console.log("Masurement", { labelWidth, labelHeight, qrSize, zoom: settings.zoom, columnGap })
  // Calculate number of columns (default to 1 if not set)
  const columns = settings.columns || 1

  // Group stickers into rows based on columns
  const rows: LotBarcodeT[][] = []
  for (let i = 0; i < barcodes?.length; i += columns) {
    rows.push(barcodes?.slice(i, i + columns))
  }

  // Calculate row width: (labelWidth * columns) + (columnGap * (columns - 1))
  const rowWidth = columns > 1 ? labelWidth * columns + columnGap * (columns - 1) : labelWidth

  return (
    <Stack
      spacing={0}
      ref={ref}
      className="single-printable-content"
      sx={{
        width: "100%",
        padding: "0 !important",
        margin: "0 !important",
        transform: `scale(${settings.zoom})`,
        transformOrigin: "top left",
        "@media print": {
          width: `${rowWidth}mm`,
          padding: "0 !important",
          margin: "0 !important",
          transform: "none",
          display: "block"
        }
      }}
    >
      {/* Rows of Stickers */}
      {rows.map((rowStickers, rowIndex) => (
        <Box
          key={rowIndex}
          sx={{
            display: "flex",
            flexDirection: "row",
            fontFamily: "OcrB",
            marginTop: rowIndex === 0 ? "0 !important" : undefined,
            paddingTop: rowIndex === 0 ? "0 !important" : undefined,
            gap: columns > 1 ? `${columnGap}mm` : "0mm",
            "@media print": {
              display: "flex",
              flexDirection: "row",

              marginBottom: `${settings.marginBottom}mm`,
              marginLeft: `${settings.marginLeft}mm`,
              marginRight: `${settings.marginRight}mm`,
              paddingTop: rowIndex === 0 ? "0mm !important" : "0mm",
              gap: columns > 1 ? `${settings.columnGap}mm` : "0mm",
              pageBreakAfter: rowIndex < rows.length - 1 ? "always" : "avoid",
              width: `${rowWidth}mm`
            }
          }}
        >
          {/* Stickers in this row */}
          {rowStickers.map((barcode, pageIndex) => (
            <Box
              key={pageIndex}
              sx={{
                display: "block",
                fontFamily: "OcrB",
                flexShrink: 0,
                "@media print": {
                  display: "block",
                  marginTop: `${settings.marginTop}mm !important`,
                  width: `${labelWidth}mm`,
                  height: `${labelHeight}mm`,
                  flexShrink: 0
                }
              }}
            >
              {/* Single Sticker - Variable Dimensions */}
              <Box
                id="sticker-wrapper"
                sx={{
                  width: `${labelWidth}mm`,
                  height: `${labelHeight}mm`,
                  padding: `${settings.padding}mm`,
                  // backgroundColor: "#e0e0e0",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  boxSizing: "border-box",
                  position: "relative",
                  gap: `${settings.qrCodeMarginRight}mm`,
                  "@media print": {
                    width: `${labelWidth}mm`,
                    height: `${labelHeight}mm`,
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: `${settings.padding}mm`,
                    // backgroundColor: "#e0e0e0",
                    printColorAdjust: "exact",
                    gap: `${settings.qrCodeMarginRight}mm`
                  }
                }}
              >
                {/* QR Code Container */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: settings.qrCodeAlignment,
                    flexShrink: 0,
                    transform: settings.orientation === "portrait" ? "rotate(-90deg)" : "none",
                    transformOrigin: "center",
                    "@media print": {
                      display: "flex",
                      alignItems: "center",
                      justifyContent: settings.qrCodeAlignment,
                      transform: settings.orientation === "portrait" ? "rotate(-90deg)" : "none",
                      transformOrigin: "center"
                    }
                  }}
                >
                  <QRCodeWithIcon
                    value={barcode?.actionId ?? ""}
                    width={`${qrSize}mm`}
                    height={`${qrSize}mm`}
                    icon={<img src={tenant?.qrInvertedLogo as string} width={`${qrSize}mm`} height={`${qrSize}mm`} />}
                    style={{ display: "block" }}
                  />
                </Box>

                {/* Text Content Container */}
                {
                  settings?.orientation === "landscape" ? (
                    <Box
                      id="text-wrapper"
                      sx={{
                        // background: "pink",
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        // justifyContent: "center",
                        textAlign: settings.textAlignment,
                        width: `${(settings.labelWidth - settings.qrCodeSize - settings.qrCodeMarginRight) * settings.zoom}mm`,

                        // transform: settings.orientation === "portrait" ? "rotate(-90deg)" : "none",
                        "@media print": {
                          display: "flex",
                          flexDirection: "column",
                          // justifyContent: "center",
                          textAlign: settings.textAlignment,
                          // transform: settings.orientation === "portrait" ? "rotate(-90deg)" : "none",
                          // transformOrigin: "center",
                          width: `${settings.labelWidth - settings.qrCodeSize - settings.qrCodeMarginRight}mm`
                        }
                      }}
                    >
                      {/* Product Name */}
                      <Typography
                        component="div"
                        sx={{
                          fontWeight: 600,
                          fontSize: `${settings.productNameFontSize}px !important`,
                          fontFamily: "OcrB",
                          lineHeight: 1.2,
                          color: "#000",
                          marginBottom: "1mm",

                          textAlign: settings.textAlignment,
                          ...(settings.productNameWrap === "single"
                            ? {
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis"
                            }
                            : {
                              display: "-webkit-box",
                              WebkitLineClamp: settings.productNameMaxLines,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              wordBreak: "break-all"
                            }),
                          "@media print": {
                            fontSize: `${settings.productNameFontSize}px !important`,
                            color: "#000",
                            textAlign: settings.textAlignment,
                            ...(settings.productNameWrap === "single"
                              ? {
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis"
                              }
                              : {
                                display: "-webkit-box",
                                WebkitLineClamp: settings.productNameMaxLines,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                wordBreak: "break-all"
                              })
                          }
                        }}
                      >
                        {barcode?.product?.name || "Product"}
                      </Typography>

                      {/* Batch Number */}
                      <Typography
                        component="div"
                        sx={{
                          fontSize: `${settings.batchNumberFontSize}px !important`,
                          fontWeight: 600,
                          color: "#000",
                          fontFamily: "OcrB",
                          textAlign: settings.textAlignment,
                          wordBreak: "break-all",
                          marginBottom: "1mm",
                          "@media print": {
                            fontSize: `${settings.batchNumberFontSize}px !important`,
                            color: "#000",
                            textAlign: settings.textAlignment
                          }
                        }}
                      >
                        {barcode?.actionId}
                      </Typography>

                      {/* Expiry Date */}
                      <Typography
                        component="div"
                        sx={{
                          fontSize: `${settings.expiryFontSize}px !important`,
                          color: "#000",
                          fontFamily: "OcrB",
                          textAlign: settings.textAlignment,
                          "@media print": {
                            fontSize: `${settings.expiryFontSize}px !important`,
                            color: "#000",
                            textAlign: settings.textAlignment
                          }
                        }}
                      >
                        Exp: {barcode?.batchExpiry}
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      id="text-wrapper"
                      sx={{
                        flex: 1,
                        position: "relative",
                        height: "100%"
                        // display: "flex",
                        // flexDirection: "column",
                        // // justifyContent: "center",
                        // textAlign: settings.textAlignment,
                        // width: `${(settings.labelWidth - settings.qrCodeSize - settings.qrCodeMarginRight) * settings.zoom}mm`,

                        // transform: settings.orientation === "portrait" ? "rotate(-90deg)" : "none",
                        // transformOrigin: "center",
                        // "@media print": {
                        //   display: "flex",
                        //   flexDirection: "column",
                        //   // justifyContent: "center",
                        //   textAlign: settings.textAlignment,
                        //   transform: settings.orientation === "portrait" ? "rotate(-90deg)" : "none",
                        //   transformOrigin: "center",
                        //   width: `${settings.labelWidth - settings.qrCodeSize - settings.qrCodeMarginRight}mm`
                        // }
                      }}
                    >
                      {/* 21.5
                      13.5 */}

                      <Box id="text-group" sx={{
                        width: (settings.labelHeight - settings?.padding - 2) * settings.zoom + "mm",
                        height: `${(settings.labelWidth - settings.qrCodeSize - settings?.padding - 2 - settings.qrCodeMarginRight) * settings.zoom}mm`,
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transformOrigin: "center",
                        transform: "translate(-50%, -50%) rotate(-90deg) scale(1)"
                      }}>
                        {/* Product Name */}
                        <Typography
                          component="div"
                          sx={{
                            fontWeight: 600,
                            fontSize: `${settings.productNameFontSize}px !important`,
                            fontFamily: "OcrB",
                            lineHeight: 1.2,
                            color: "#000",
                            marginBottom: "1mm",

                            textAlign: settings.textAlignment,
                            ...(settings.productNameWrap === "single"
                              ? {
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis"
                              }
                              : {
                                display: "-webkit-box",
                                WebkitLineClamp: settings.productNameMaxLines,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                wordBreak: "break-all"
                              }),
                            "@media print": {
                              fontSize: `${settings.productNameFontSize}px !important`,
                              color: "#000",
                              textAlign: settings.textAlignment,
                              ...(settings.productNameWrap === "single"
                                ? {
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis"
                                }
                                : {
                                  display: "-webkit-box",
                                  WebkitLineClamp: settings.productNameMaxLines,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                  wordBreak: "break-all"
                                })
                            }
                          }}
                        >
                          {barcode?.product?.name || "Product"}
                        </Typography>

                        {/* Batch Number */}
                        <Typography
                          component="div"
                          sx={{
                            fontSize: `${settings.batchNumberFontSize}px !important`,
                            fontWeight: 600,
                            color: "#000",
                            fontFamily: "OcrB",
                            textAlign: settings.textAlignment,
                            wordBreak: "break-all",
                            marginBottom: "1mm",
                            "@media print": {
                              fontSize: `${settings.batchNumberFontSize}px !important`,
                              color: "#000",
                              textAlign: settings.textAlignment
                            }
                          }}
                        >
                          {barcode?.actionId}
                        </Typography>

                        {/* Expiry Date */}
                        <Typography
                          component="div"
                          sx={{
                            fontSize: `${settings.expiryFontSize}px !important`,
                            color: "#000",
                            fontFamily: "OcrB",
                            textAlign: settings.textAlignment,
                            "@media print": {
                              fontSize: `${settings.expiryFontSize}px !important`,
                              color: "#000",
                              textAlign: settings.textAlignment
                            }
                          }}
                        >
                          Exp: {barcode?.batchExpiry}
                        </Typography>
                      </Box>
                    </Box>)
                }

                {/* White vertical strip on the right */}
                {/* <Box
              sx={{
                width: `${settings.whiteStripWidth}mm`,
                height: "100%",
                backgroundColor: "#ffffff",
                position: "absolute",
                right: "0mm",
                top: "0mm",
                display: "block",
                "@media print": {
                  display: "block",
                  width: `${settings.whiteStripWidth}mm`,
                  height: "100%",
                  backgroundColor: "#ffffff",
                  printColorAdjust: "exact"
                }
              }}
            /> */}

                {/* <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "flex-start",
                flex: 1,
                marginLeft: "2mm",
                height: "100%"
              }}
            >
              <Typography
                sx={{
                  fontSize: "4px",
                  fontFamily: "OcrB",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                  lineHeight: 1,
                  marginBottom: "1mm"
                }}
              >
                {config.id}~{pageIndex + 1}
              </Typography>

              <Typography
                sx={{
                  fontSize: "4px",
                  fontWeight: "bold",
                  fontFamily: "OcrB",
                  wordBreak: "break-word",
                  maxWidth: "25mm",
                  overflow: "hidden",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  lineHeight: 1.2,
                  marginBottom: "1mm"
                }}
              >
                {config.name || "Product"}
              </Typography>

              <Typography
                sx={{
                  fontSize: "4px",
                  fontFamily: "OcrB",
                  fontWeight: "bold",
                  whiteSpace: "nowrap",
                  lineHeight: 1
                }}
              >
                EXP:{config.expiry}
              </Typography>
            </Box> */}
              </Box>
            </Box>
          ))}
        </Box>
      ))}
    </Stack>
  )
})

BarcodeLayout.displayName = "BarcodeLayout"

export default BarcodeLayout
